import { connectorsForWallets } from "@rainbow-me/rainbowkit";
import {
  coinbaseWallet,
  injectedWallet,
  metaMaskWallet,
  rabbyWallet,
  rainbowWallet,
  walletConnectWallet,
} from "@rainbow-me/rainbowkit/wallets";
import { createConfig, createConnector, http } from "wagmi";
import { injected } from "wagmi/connectors";
import { defineChain } from "viem";

import { mudwaterEvmChainConfig, mudwaterEvmChainId } from "@/lib/mudwater";
import {
  browserWalletEntries,
  seedBrowserWalletChoice,
} from "@/lib/wallet/browser-wallet";
import {
  gatedMetaMaskEntry,
  injectedMetaMask,
  injectedProviders,
  rabbyEntry,
  seedMetaMaskChoice,
} from "@/lib/wallet/evm-gated-entry";
import { braveEntry } from "@/lib/wallet/brave-evm";
import { glyphEvmWallets } from "@/lib/wallet/glyph-evm";
import { hasPhantomEthereum, phantomEntry } from "@/lib/wallet/phantom-evm";
import { phantomInFront } from "@/lib/wallet/phantom-genuine";
import {
  isWalletConnectConfigured,
  walletConnectProjectId,
} from "@/lib/wallet/config";
import { isOffered } from "@/lib/wallet/solana/allowlist";

import type { Wallet, WalletDetailsParams } from "@rainbow-me/rainbowkit";
import type { Config } from "wagmi";

/**
 * `injectedWallet` declares no `installed`, and RainbowKit reads that as
 * `ready: installed ?? true` — so "Browser Wallet" showed first in the picker
 * on a phone, where an ordinary browser tab never has an injected provider.
 * Tapping it did nothing at all: the connector has no provider to reach, and
 * unlike every other entry it has no deep link to fall back on, so it was the
 * same silent dead end the MetaMask entry was.
 *
 * Reporting the truth withdraws it wherever there is nothing behind it — the
 * mobile sheet lists only `ready` wallets, and the desktop list only
 * `ready || extensionDownloadUrl`, which this wallet has none of — and leaves
 * it exactly where it earns its place: a browser wallet announcing none of the
 * rdns named below, and a wallet app's own in-app browser, both of which set
 * `window.ethereum`. One that announces itself over EIP-6963 instead is
 * already listed from that announcement.
 *
 * Read per call rather than once at module scope: `createWagmiConfig` runs on
 * the server too, where there is no `window` to ask.
 */
function hasInjectedProvider(): boolean {
  if (typeof window === "undefined") return false;
  const provider: unknown = Reflect.get(window, "ethereum");
  return provider !== undefined && provider !== null;
}

export function browserWallet(): Wallet {
  return {
    ...injectedWallet(),
    installed: hasInjectedProvider() && !phantomCoversIt(),
  };
}

/**
 * Whether the only thing behind `window.ethereum` is Phantom, which now has an
 * entry of its own.
 *
 * Listing both would put one wallet in the picker twice, and the two entries
 * are not equivalent: "Browser Wallet" connects it as an ungated injected
 * connector targeted at whatever wallet is in front — not the targetless one
 * this once assumed — so a holder who reached Phantom that way would have it
 * reconnected on every load by a connector that cannot tell Phantom from
 * anything else. The named entry below is the one that remembers the choice
 * was Phantom's and can withdraw it again. That connector exists even where
 * this entry is not shown, which `browserWalletReachesPhantom` closes.
 *
 * Only when the named entry would actually appear, and only when nothing else
 * is injected: a browser holding MetaMask beside Phantom still needs this
 * entry, and the two cannot be told apart once they are behind one `providers`
 * array that this wallet does not pick from.
 */
function phantomCoversIt(): boolean {
  if (!hasPhantomEthereum()) return false;
  const providers = injectedProviders(Reflect.get(window, "ethereum"));
  return providers.length > 0 && providers.every(phantomInFront);
}

/**
 * RainbowKit's MetaMask entry is backed by the MetaMask SDK, and that SDK
 * cannot be reconnected once it has been disconnected.
 *
 * Its `terminate()` — what wagmi's connector calls on disconnect — revokes the
 * site's `eth_accounts` permission and then, unless `extensionOnly` is set,
 * swaps `activeProvider` from the extension back to its own remote (QR and
 * deeplink) provider. wagmi always passes an options object, so the SDK's own
 * `extensionOnly: true` default never applies, and the swap always happens.
 * The next `connect()` therefore asks the *remote* provider for accounts; with
 * the `headless: true` RainbowKit sets, the SDK shows no modal of its own and
 * nothing renders the pairing URI — so the picker sits on "Opening MetaMask…"
 * for good and the extension is never asked anything. RainbowKit's wagmi
 * connector caches both the SDK and its provider in a closure, so only a
 * reload clears it. That is the whole of the bug behind reconnecting after
 * "Use a different wallet".
 *
 * Where MetaMask is injected, the SDK buys nothing: the extension speaks
 * EIP-1193 directly, and wagmi's injected connector reaches it through the
 * same `isMetaMask` detection the SDK uses. It also reconnects properly —
 * `shimDisconnect` asks for `wallet_requestPermissions`, which is the prompt
 * that lets a holder hand over a different account, which is exactly what the
 * holder asked for by disconnecting.
 *
 * Everything else about the entry is RainbowKit's: name, icon, rdns and
 * download links all still come from `metaMaskWallet`, and where MetaMask is
 * *not* injected — a phone browser, a desktop without the extension — it is
 * returned untouched, so the QR and deeplink paths are unchanged. Those paths
 * never reach the terminated-SDK state anyway: they are WalletConnect.
 */
export function metaMaskEntry(
  params: Parameters<typeof metaMaskWallet>[0]
): Wallet {
  const wallet = metaMaskWallet(params);
  // Offered for Solana too: gated, and never revoking (`evm-gated-entry.ts`).
  if (isOffered("metamask"))
    return injectedMetaMask() ? gatedMetaMaskEntry(wallet) : wallet;
  if (!hasMetaMaskExtension()) return wallet;
  return {
    ...wallet,
    installed: true,
    createConnector: (walletDetails: WalletDetailsParams) =>
      createConnector((config) => ({
        ...injected({ target: "metaMask" })(config),
        ...walletDetails,
      })),
  };
}

/**
 * The same question `injected({ target: "metaMask" })` asks when it looks for a
 * provider, asked early so the entry can decide which connector to carry.
 * Wallets that set `isMetaMask` to pass for it are excluded by their own flag —
 * Phantom is the one that matters here, since the site asks holders to install
 * it for Solana, and it would otherwise answer for MetaMask in a browser where
 * MetaMask is absent. A wrong answer here is not silent: the injected
 * connector reports no provider and the picker says so, rather than hanging.
 */
function hasMetaMaskExtension(): boolean {
  if (typeof window === "undefined") return false;
  return injectedProviders(Reflect.get(window, "ethereum")).some(isMetaMask);
}

function isMetaMask(provider: unknown): boolean {
  if (typeof provider !== "object" || provider === null) return false;
  const flag = (name: string) => Boolean(Reflect.get(provider, name));
  return (
    flag("isMetaMask") &&
    !flag("isPhantom") &&
    !flag("isBraveWallet") &&
    !flag("isRabby")
  );
}

/** Derived from the same settings the rest of the app reads, so the wallet's
 * network and the SDK's expected network cannot drift. */
export const mudwaterChain = defineChain({
  id: mudwaterEvmChainId,
  name: mudwaterEvmChainConfig.chainName,
  nativeCurrency: mudwaterEvmChainConfig.nativeCurrency,
  rpcUrls: { default: { http: [mudwaterEvmChainConfig.rpcUrl] } },
  ...(mudwaterEvmChainConfig.blockExplorerUrl
    ? {
        blockExplorers: {
          default: {
            name: `${mudwaterEvmChainConfig.chainName} explorer`,
            url: mudwaterEvmChainConfig.blockExplorerUrl,
          },
        },
      }
    : {}),
});

/**
 * The wallet list is explicit rather than RainbowKit's default set, so the
 * picker never offers Base Account: `next.config.ts` resolves
 * `@coinbase/cdp-sdk` away, and a wallet whose SDK is stubbed does not belong
 * in the list. That stub reaches only the SDK's Base Pay and subscription
 * modules, so connecting would probably survive it, but nothing here needs it.
 *
 * This list is not what keeps the build compiling; the aliases are. RainbowKit's
 * entry imports `baseAccount` from `wagmi/connectors` at the top level, so the
 * cdp-sdk subtree and its unresolvable `@x402/*` imports are in the module graph
 * whatever this names — dropping the aliases fails the build even with just the
 * seven below. Against the default set this adds `coinbaseWallet`,
 * `rabbyWallet`, `phantomEntry` and `injectedWallet` — as `browserWallet`,
 * which is the same
 * entry taught to report whether anything is actually injected — the last
 * covering a browser wallet that announces none of the rdns named here, and
 * omits `safeWallet`, which only ever shows inside a Safe App iframe.
 *
 * MetaMask is listed as `metaMaskEntry`, which is RainbowKit's own entry
 * wherever the extension is absent and the same entry on an injected connector
 * wherever it is not.
 *
 * `rabbyWallet` resolves its provider by the `isRabby` flag rather than through
 * EIP-6963, so it needs nothing from the discovery switched off below and
 * cannot reach Phantom, which never sets that flag. Without it Rabby was
 * reachable only as `browserWallet`, and only when it held `window.ethereum`.
 *
 * `phantomEntry` is RainbowKit's Phantom on a connector that has no provider
 * until the holder picks it, which is what lets the one wallet this site asks
 * holders to install for another chain be listed here without reopening the
 * connection it used to take on its own. Where it is the only wallet injected,
 * `browserWallet` stands down rather than list it twice.
 *
 * Separately, `browserWallet` is left out of the list altogether wherever it
 * would reach Phantom and Phantom's own entry can (`browserWalletReachesPhantom`).
 * Standing down only hides a row: RainbowKit still builds a connector for every
 * listed entry, and that one would target Phantom ungated.
 */
function connectors() {
  // RainbowKit validates the project ID whenever `connectorsForWallets` runs,
  // whatever wallets are listed, so an unconfigured build uses wagmi's injected
  // connector directly rather than a placeholder ID.
  if (!isWalletConnectConfigured) return [injected()];

  return connectorsForWallets(
    [
      {
        groupName: "Wallets",
        wallets: [
          // Never a second, ungated route to Phantom, then D6
          // (`browser-wallet.ts`).
          ...browserWalletEntries(browserWallet),
          ...glyphEvmWallets(),
          braveEntry,
          metaMaskEntry,
          phantomEntry,
          () => rabbyEntry(rabbyWallet()),
          coinbaseWallet,
          rainbowWallet,
          walletConnectWallet,
        ],
      },
    ],
    { appName: "Mudwater", projectId: walletConnectProjectId }
  );
}

/**
 * Built per mount rather than cached in the module: the live provider renders
 * on the server too, and one config shared between requests is a mutable
 * singleton in a process that serves everybody. A demo build imports this
 * module but never calls this, so it pays nothing for connector discovery.
 *
 * `ssr: true` is correct even though nothing server-renders wallet state: it
 * defers storage hydration to an effect, so the first client render matches the
 * prerendered markup instead of reporting a reconnect mid-hydration.
 */
export function createWagmiConfig(): Config {
  // Before `createConfig`, whose `setup()` calls are the gates' first read.
  if (isOffered("metamask")) seedMetaMaskChoice();
  seedBrowserWalletChoice();
  return createConfig({
    chains: [mudwaterChain],
    connectors: connectors(),
    transports: { [mudwaterChain.id]: http() },
    ssr: true,
    /*
     * Off, which it is not by default.
     *
     * `reconnect` tries every configured connector and adopts the first that
     * reports an account, and EIP-6963 discovery puts every announcing wallet
     * in that list. Connecting Phantom for Solana authorises Phantom for the
     * site, so its *Ethereum* side starts reporting an account — and wagmi
     * adopted it as the ApeChain connection without the holder ever choosing
     * it. Disconnect MetaMask and reload, and the claim page checked a
     * Phantom address for BAYC it was never going to hold.
     *
     * The curated list above stays, `browserWallet` included, so nothing
     * becomes unreachable: a wallet that announces only over EIP-6963 is
     * still reached through the injected entry. What it cannot do any more is
     * join the connection list on its own.
     *
     * Phantom is now *in* that list, which does not undo any of this. The
     * flag decides who may enter the list; `phantomEntry` decides what being
     * in it is worth without the holder's say-so, which for Phantom is
     * nothing until they pick it. Both are needed: this flag alone would let
     * every other announcing wallet back in, and the entry alone would leave
     * discovery registering a second, ungated Phantom connector beside it.
     */
    multiInjectedProviderDiscovery: false,
  });
}
